import { useEffect, useCallback, useMemo } from 'react';
import { useLanguageContext } from '../../../../../contexts/LanguageContext';
import Dialog from '../../../../../components/Dialog';
import { ActionButton } from '../../../../../components/Button';
import { Grid } from '@mui/material';
import DefaultConstants from '../../../../../data/Constants';
import { IDefaultDialogFilterProps } from '../../../../../interfaces/IReport';
import DateTimeHelper from '../../../../../helpers/DateTimeHelper';
import DateField from '../../../../../components/DateField';
import SelectField from '../../../../../components/SelectField';
import { useAuthContext } from '../../../../../contexts/AuthContext';
import IEnterprise from '../../../../../interfaces/IEnterprise';
import { IBlock, IFloor, IRoom, IRoomType } from '../../../../../interfaces/IPartnerPms';
import IUser from '../../../../../interfaces/IUser';
import { GovernanceStatusData, IDefaultStatusData, RoomLogTypeData, RoomLogTypeEnum, RoomStatusData, RoomStatusEnum } from '../../../../../data/Enums';
import useState from '../../../../../hooks/useState';


export interface IRoomLogReportFilters {
    enterprise: IEnterprise | undefined;
    room: IRoom[];
    user: IUser[];
    block: IBlock[];
    floor: IFloor[];
    roomType: IRoomType[];
    startDate: string;
    endDate: string;
    action: RoomLogTypeEnum[];
    initialStatus: IDefaultStatusData[];
    finalStatus: IDefaultStatusData[];
}


interface IRoomLogReportDialogFilterProps extends IDefaultDialogFilterProps {
    userList: IUser[];
}

const RoomLogFilters: React.FunctionComponent<IRoomLogReportDialogFilterProps> = ({
    open, 
    handleClose,
    handleOpenPreview,
    enterpriseList,
    userList
}) => {
    const { translate } = useLanguageContext();
    const { enterprise } = useAuthContext();
    
    const state = useState<IRoomLogReportFilters>({
        enterprise: enterprise,
        room: [],
        user: [],
        block: [],
        floor: [],
        roomType: [],
        startDate: DateTimeHelper.Now('YYYY-MM-DD'),
        endDate: DateTimeHelper.Now('YYYY-MM-DD'), 
        action: [],
        initialStatus: [], 
        finalStatus: []
    });


    useEffect(() => {
        if (open) {
            state.reset();
        }
        // eslint-disable-next-line
    }, [open]);


    useEffect(() => {
        state.update('room', []);
        state.update('user', []);
        // eslint-disable-next-line
    }, [state.values.enterprise]);


    const enterprisePartnerPms = useMemo(() => {
        return {
            rooms: state.values.enterprise?.partnerPms.rooms ?? [],
            users: userList.filter(u => u.enterprises.map(e => e.id).includes(state.values.enterprise?.id ?? 0)) ?? [],
            blocks: state.values.enterprise?.partnerPms.blocks ?? [],
            floors: state.values.enterprise?.partnerPms.floors ?? [],
            roomTypes: state.values.enterprise?.partnerPms.roomTypes ?? [],
        }
    }, [state.values.enterprise, userList]);


    const roomLogPossibleStatus = useMemo(() => {
        const action = state.values.action;
        let status = [] as IDefaultStatusData[];

        if (action.length === 0 || action.includes(RoomLogTypeEnum.UpdateGovernanceStatus)) {
            status = status.concat(...Object.values(GovernanceStatusData).filter(x => x.partnerId));
        }

        if (action.length === 0 || action.includes(RoomLogTypeEnum.UpdateDoNotDisturb)) {
            const doNotDisturb = RoomStatusData[RoomStatusEnum.DoNotDisturb];

            status.push({
                ...doNotDisturb,
                key: 998,
                name: `${doNotDisturb.name}Enabled`,
                partnerId: 'true'
            });

            status.push({
                ...doNotDisturb,
                key: 999,
                name: `${doNotDisturb.name}Disabled`,
                partnerId: 'false'
            });
        }

        return status;
    }, [state.values.action]);


    const dialogActions = useMemo(() => {
        return [
            <ActionButton action='close' onClick={handleClose} />,
            <ActionButton action='view' type='submit' />
        ];
        // eslint-disable-next-line
    }, [translate]);


    const handleSubmit = useCallback((event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const _filters = state.values;
        const startDate = _filters.startDate;
        const endDate = _filters.endDate;

        if (startDate && endDate && startDate > endDate) {
            const aux = startDate;
            _filters.startDate = endDate;
            _filters.endDate = aux;
        }

        handleOpenPreview(_filters);

        state.update('startDate', _filters.startDate);
        state.update('endDate', _filters.endDate);
        // eslint-disable-next-line
    }, [state.values]);


    return (
        <Dialog 
            open={open}
            handleClose={handleClose}
            title={translate('roomLog')}
            fullWidth={true}
            dialogActions={dialogActions}
            onSubmit={handleSubmit}
            className='dialogactions-spacebetween'
            maxWidth='md'
            fullHeight={true}
        >
            <Grid container rowSpacing={DefaultConstants.gridRowSpacing} columnSpacing={DefaultConstants.gridColumnSpacing}>
                <Grid item lg={6} md={6} sm={12} xs={12}>
                    <SelectField
                        label={translate('enterprise')}
                        name='enterprise'
                        value={state.values.enterprise?.id ?? 0}
                        onChange={e => {
                            const { name, value } = e.target;
                            const enterprise = enterpriseList.find(e => +e.id === +value);
                            state.update(name, enterprise);
                        }}
                        required={true}
                        data={enterpriseList.map(e => {
                            return {
                                value: +e.id,
                                text: e.name
                            };
                        })}
                    />
                </Grid>

                <Grid item lg={3} md={3} sm={6} xs={6}>
                    <DateField
                        label={translate('startDate')}
                        format='DD/MM/YYYY'
                        name='startDate'
                        value={DateTimeHelper.ToDate(state.values.startDate)}
                        onChange={value => state.update('startDate', DateTimeHelper.Format(value, 'YYYY-MM-DD'))}
                        required={true}
                        disableFuture={true}
                    />
                </Grid>

                <Grid item lg={3} md={3} sm={6} xs={6}>
                    <DateField
                        label={translate('endDate')}
                        format='DD/MM/YYYY'
                        name='endDate'
                        value={DateTimeHelper.ToDate(state.values.endDate)}
                        onChange={value => state.update('endDate', DateTimeHelper.Format(value, 'YYYY-MM-DD'))}
                        required={true}
                        disableFuture={true}
                    />
                </Grid>

                <Grid item xs={12} />

                <Grid item lg={4} md={4} sm={12} xs={12}>
                    <SelectField
                        label={translate('action')}
                        name='action'
                        value={state.values.action.length > 0 ? state.values.action : ['']}
                        onChange={e => {
                            const { name, value } = e.target;
                            const values = value as string[];

                            const action = state.values.action.length > 0 && values.includes('') ? [] : Object.values(RoomLogTypeData).filter(t => values.includes(t.key)).map(s => s.key);
                            state.update(name, action);
                        }}
                        required={false}
                        multiple={true}
                        data={[{ value: '', text: translate('all') }].concat(Object.values(RoomLogTypeData).map(t => {
                            return {
                                value: t.key,
                                text: translate(t.name)
                            };
                        }))}
                    />
                </Grid>

                <Grid item lg={4} md={4} sm={6} xs={12}>
                    <SelectField
                        label={translate('initialStatus')}
                        name='initialStatus'
                        value={state.values.initialStatus.length > 0 ? state.values.initialStatus.map(s => +s.key) : [-1]}
                        onChange={e => {
                            const { name, value } = e.target;
                            const values = value as number[];

                            const status = state.values.initialStatus.length > 0 && values.includes(-1) ? [] : roomLogPossibleStatus.filter(s => values.includes(+s.key));
                            state.update(name, status);
                        }}
                        required={false}
                        multiple={true}
                        data={[{ value: -1, text: translate('all') }].concat(roomLogPossibleStatus.map(s => {
                            return {
                                value: +s.key,
                                text: translate(s.name)
                            };
                        }))}
                    />
                </Grid>

                <Grid item lg={4} md={4} sm={6} xs={12}>
                    <SelectField
                        label={translate('finalStatus')}
                        name='finalStatus'
                        value={state.values.finalStatus.length > 0 ? state.values.finalStatus.map(s => +s.key) : [-1]}
                        onChange={e => {
                            const { name, value } = e.target;
                            const values = value as number[];

                            const status = state.values.finalStatus.length > 0 && values.includes(-1) ? [] : roomLogPossibleStatus.filter(s => values.includes(+s.key));
                            state.update(name, status);
                        }}
                        required={false}
                        multiple={true}
                        data={[{ value: -1, text: translate('all') }].concat(roomLogPossibleStatus.map(s => {
                            return {
                                value: +s.key,
                                text: translate(s.name)
                            };
                        }))}
                    />
                </Grid>

                <Grid item lg={4} md={4} sm={6} xs={12}>
                    <SelectField
                        label={translate('user')}
                        name='user'
                        value={state.values.user.length > 0 ? state.values.user.map(r => +r.id) : [-1]}
                        required={false}
                        multiple={true}
                        onChange={e => {
                            const { name, value } = e.target;
                            const values = value as number[];

                            const user = state.values.user.length > 0 && values.includes(-1) ? [] : enterprisePartnerPms.users.filter(r => values.includes(+r.id));
                            state.update(name, user);
                        }}
                        data={[{ value: -1, text: translate('all') }].concat(enterprisePartnerPms.users.map(r => {
                            return {
                                value: +r.id,
                                text: r.fullName
                            };
                        }))}
                    />
                </Grid>

                <Grid item lg={4} md={4} sm={6} xs={12}>
                    <SelectField
                        label={translate('room')}
                        name='room'
                        value={state.values.room.length > 0 ? state.values.room.map(r => +r.id) : [-1]}
                        required={false}
                        multiple={true}
                        onChange={e => {
                            const { name, value } = e.target;
                            const values = value as number[];

                            const room = state.values.room.length > 0 && values.includes(-1) ? [] : enterprisePartnerPms.rooms.filter(r => values.includes(+r.id));
                            state.update(name, room);
                        }}
                        data={[{ value: -1, text: translate('all') }].concat(enterprisePartnerPms.rooms.map(r => {
                            return {
                                value: +r.id,
                                text: r.description
                            };
                        }))}
                    />
                </Grid>

                <Grid item lg={4} md={4} sm={6} xs={12}>
                    <SelectField
                        label={translate('block')}
                        name='block'
                        value={state.values.block.length > 0 ? state.values.block.map(b => +b.id) : [-1]}
                        required={false}
                        multiple={true}
                        onChange={e => {
                            const { name, value } = e.target;
                            const values = value as number[];

                            const block = state.values.block.length > 0 && values.includes(-1) ? [] : enterprisePartnerPms.blocks.filter(b => values.includes(+b.id));
                            state.update(name, block);
                        }}
                        data={[{ value: -1, text: translate('all') }].concat(enterprisePartnerPms.blocks.map(b => {
                            return {
                                value: +b.id,
                                text: b.description
                            };
                        }))}
                    />
                </Grid>

                <Grid item lg={4} md={4} sm={6} xs={12}>
                    <SelectField
                        label={translate('floor')}
                        name='floor'
                        value={state.values.floor.length > 0 ? state.values.floor.map(f => +f.id) : [-1]}
                        required={false}
                        multiple={true}
                        onChange={e => {
                            const { name, value } = e.target;
                            const values = value as number[];

                            const floor = state.values.floor.length > 0 && values.includes(-1) ? [] : enterprisePartnerPms.floors.filter(f => values.includes(+f.id));
                            state.update(name, floor);
                        }}
                        data={[{ value: -1, text: translate('all') }].concat(enterprisePartnerPms.floors.map(f => {
                            return {
                                value: +f.id,
                                text: f.description
                            };
                        }))}
                    />
                </Grid>

                <Grid item lg={4} md={4} sm={6} xs={12}>
                    <SelectField
                        label={translate('roomType')}
                        name='roomType'
                        value={state.values.roomType.length > 0 ? state.values.roomType.map(r => +r.id) : [-1]}
                        required={false}
                        multiple={true}
                        onChange={e => {
                            const { name, value } = e.target;
                            const values = value as number[];

                            const roomType = state.values.roomType.length > 0 && values.includes(-1) ? [] : enterprisePartnerPms.roomTypes.filter(r => values.includes(+r.id));
                            state.update(name, roomType);
                        }}
                        data={[{ value: -1, text: translate('all') }].concat(enterprisePartnerPms.roomTypes.map(r => {
                            return {
                                value: +r.id,
                                text: r.description
                            };
                        }))}
                    />
                </Grid>
            </Grid>
        </Dialog>
    );
}

export default RoomLogFilters;